import type { RoutePath } from '../api/types'
import { t } from '../i18n/lingua'

/** Un nodo del grafo del percorso. */
interface Nodo {
  label: string
  sub?: string
  kind: 'host' | 'device' | 'cloud' | 'drop'
}

/** Glifo e colore per tipo di nodo. */
function stile(kind: Nodo['kind']): { glifo: string; fill: string; stroke: string } {
  switch (kind) {
    case 'host':
      return { glifo: '🖥', fill: 'var(--surface-2, #eef2f7)', stroke: 'var(--border, #c7cfda)' }
    case 'cloud':
      return { glifo: '☁', fill: 'rgba(120,160,255,0.12)', stroke: 'var(--border, #c7cfda)' }
    case 'drop':
      return { glifo: '⛔', fill: 'rgba(220,80,80,0.12)', stroke: 'var(--warn, #d9822b)' }
    default:
      return { glifo: '🖧', fill: 'rgba(90,160,120,0.14)', stroke: 'var(--ok, #3a9)' }
  }
}

/**
 * Il percorso come **grafo** (stile GNS3), orizzontale: sorgente → apparati →
 * destinazione. I nodi e i link si rivelano uno alla volta (guidati da
 * `visibili`) e ogni link «cresce» dalla sorgente al next-hop — l'effetto del
 * pacchetto che avanza. Autonomo: SVG puro, nessuna libreria.
 */
export function RoutePathGraph({ path, visibili }: { path: RoutePath; visibili: number }) {
  const nodi: Nodo[] = [{ label: path.src, kind: 'host' }]
  path.hops.forEach((h) =>
    nodi.push({ label: h.ci_name, sub: h.matched ?? t('senza tabella'), kind: 'device' }),
  )
  // Il nodo finale dipende dall'esito.
  if (path.outcome === 'reached' || path.outcome === 'same_subnet') {
    nodi.push({ label: path.dst, kind: 'host' })
  } else if (path.outcome === 'exits') {
    nodi.push({ label: t('esterno'), sub: path.dst, kind: 'cloud' })
  } else if (path.outcome === 'no_route' || path.outcome === 'loop') {
    nodi.push({ label: path.outcome === 'loop' ? t('anello') : t('nessuna rotta'), kind: 'drop' })
  }

  // Etichette dei link: il next-hop di ciascun salto (o «connessa»).
  const etichette: string[] = path.hops.map((h) => h.next_hop ?? (h.matched ? t('connessa') : '?'))
  // Se c'è un nodo finale oltre gli hop, l'ultimo link non ha un salto proprio.
  if (nodi.length > path.hops.length + 1) etichette.push('')

  const W = 190
  const GAP = 70
  const H = 92
  const larghezza = nodi.length * W + (nodi.length - 1) * GAP + 40
  const cy = H / 2

  const x = (i: number) => 20 + i * (W + GAP)

  return (
    <div className="table-wrap" style={{ padding: '4px 0' }}>
      <svg width={larghezza} height={H + 24} role="img" aria-label={t('Grafo del percorso')}>
        {/* Link */}
        {nodi.slice(1).map((_, i) => {
          const x1 = x(i) + W
          const x2 = x(i + 1)
          const attivo = i + 1 <= visibili
          const len = x2 - x1
          return (
            <g key={`e${i}`}>
              <line
                x1={x1}
                y1={cy}
                x2={x2}
                y2={cy}
                stroke="var(--border, #b7c0cc)"
                strokeWidth={2}
                strokeDasharray={len}
                strokeDashoffset={attivo ? 0 : len}
                style={{ transition: 'stroke-dashoffset .3s ease' }}
              />
              {/* freccia */}
              <polygon
                points={`${x2 - 7},${cy - 4} ${x2},${cy} ${x2 - 7},${cy + 4}`}
                fill="var(--border, #b7c0cc)"
                style={{ opacity: attivo ? 1 : 0, transition: 'opacity .3s' }}
              />
              {etichette[i] && (
                <text
                  x={(x1 + x2) / 2}
                  y={cy - 8}
                  textAnchor="middle"
                  fontSize={11}
                  fill="var(--text-muted, #7a8699)"
                  style={{ opacity: attivo ? 1 : 0, transition: 'opacity .3s' }}
                >
                  {etichette[i]}
                </text>
              )}
            </g>
          )
        })}

        {/* Nodi */}
        {nodi.map((n, i) => {
          const s = stile(n.kind)
          const visibile = i <= visibili
          return (
            <g
              key={`n${i}`}
              transform={`translate(${x(i)}, ${cy - 26})`}
              style={{ opacity: visibile ? 1 : 0.15, transition: 'opacity .3s' }}
            >
              <rect width={W} height={52} rx={8} fill={s.fill} stroke={s.stroke} strokeWidth={1.5} />
              <text x={16} y={31} fontSize={20}>
                {s.glifo}
              </text>
              <text x={44} y={22} fontSize={13} fontWeight={600} fill="var(--text, #223)">
                {n.label.length > 20 ? n.label.slice(0, 19) + '…' : n.label}
              </text>
              {n.sub && (
                <text x={44} y={38} fontSize={11} fill="var(--text-muted, #7a8699)" className="mono">
                  {n.sub}
                </text>
              )}
            </g>
          )
        })}
      </svg>
    </div>
  )
}
